/**
 * Per-surface billing step config (Phase 9.8G).
 * Aligns creation / historic flows with billingRetrievalConfig patterns from Phase 9.1.
 */

const isNonUsCitizenship = (value) => {
  if (!value) return false;
  const normalized = value.toLowerCase();
  return normalized !== "usa" && normalized !== "united states";
};

const hasForeignCitizenshipDefault = (guardians) =>
  guardians?.some(
    (guardian) =>
      isNonUsCitizenship(guardian.citizenship) ||
      isNonUsCitizenship(guardian.custom_citizenship)
  );

const hasForeignCitizenshipGuardianship = (guardians) =>
  guardians?.some(
    (guardian) =>
      isNonUsCitizenship(guardian.guardian_citizenship) ||
      isNonUsCitizenship(guardian.custom_guardian_citizenship)
  );

const hasPgPreferredBilling = (guardians) =>
  guardians?.some((guardian) => guardian.preferred_billing === true);

const defaultPrincipalMapper = (guardian) => ({
  label: `${guardian.name} - ${guardian.address}, ${guardian.city}, ${guardian.state}, ${guardian.zip_code}`,
  value: guardian.name,
});

const guardianshipPrincipalMapper = (guardian) => ({
  label: `${guardian.guardian_name} - ${guardian.guardian_street_address_1}, ${guardian.guardian_city}, ${guardian.guardian_state}, ${guardian.guardian_zip}`,
  value: guardian.guardian_name,
});

const formatLawFirmLocation = (location) => ({
  label: `${location.name} - ${location.address}`,
  value: location.law_firm_location_id,
  address: location.address,
});

export { defaultPrincipalMapper, guardianshipPrincipalMapper, formatLawFirmLocation };

export function buildBillingPayload(info, { includeBillingOverride = false } = {}) {
  const payload = {
    billing_entity: info.billing_entity,
    billing_lawfirmaddress_name:
      info.billing_entity === "Law Firm" ? (info.billing_lawfirmaddress_name || "") : "",
    billing_principal_name:
      info.billing_entity === "Principal" ? (info.billing_principal_name || "") : "",
    billing_other_name: info.billing_entity === "Other" ? (info.billing_other_name || "") : "",
    billing_other_address:
      info.billing_entity === "Other" ? (info.billing_other_address || "") : "",
    billing_other_relationship:
      info.billing_entity === "Other" ? (info.billing_other_relationship || "") : "",
    billing_other_city: info.billing_entity === "Other" ? (info.billing_other_city || "") : "",
    billing_other_state: info.billing_entity === "Other" ? (info.billing_other_state || "") : "",
    billing_other_zip: info.billing_entity === "Other" ? (info.billing_other_zip || "") : "",
  };

  if (includeBillingOverride) {
    payload.is_billing_override = info.is_billing_override ?? false;
  }

  return payload;
}

export function buildRetrievalBillingPayload(info) {
  return {
    ...buildBillingPayload(info),
    custNo:
      info.billing_entity === "Other" || info.billing_entity === "Principal"
        ? (info.custNo || "")
        : "",
  };
}

export function getInitialBillingEntity({
  cfg,
  globalData,
  isGuest,
  guardians,
}) {
  const attorneyInfo = globalData?.attorneyInformation || {};
  const foreign = cfg.hasForeignCitizenship(guardians);

  if (isGuest && cfg.supportsGuestFlow) {
    return "Law Firm";
  }

  if (foreign) {
    return "Law Firm";
  }

  if (cfg.supportsPgPreferredBilling && hasPgPreferredBilling(guardians)) {
    return "Principal";
  }

  return attorneyInfo.attorneyInvolved === "Yes" ? "Law Firm" : "Principal";
}

export const BILLING_SURFACE_CONFIG = {
  creation: {
    surface: "creation",
    mapPrincipal: defaultPrincipalMapper,
    formatLawFirmLocation,
    hasForeignCitizenship: hasForeignCitizenshipDefault,
    showInvoiceSubtitle: true,
    supportsPgPreferredBilling: true,
    supportsBillingOverride: true,
    supportsGuestFlow: true,
    historicFetchEntitySideEffects: false,
    includeBillingOverrideInPayload: true,
    headingClassName: "font-bold mb-2",
  },
  creationGuardianship: {
    surface: "creationGuardianship",
    mapPrincipal: guardianshipPrincipalMapper,
    formatLawFirmLocation,
    hasForeignCitizenship: hasForeignCitizenshipGuardianship,
    showInvoiceSubtitle: true,
    supportsPgPreferredBilling: true,
    supportsBillingOverride: true,
    supportsGuestFlow: true,
    historicFetchEntitySideEffects: false,
    includeBillingOverrideInPayload: true,
    headingClassName: "font-bold mb-2",
  },
  historic: {
    surface: "historic",
    mapPrincipal: defaultPrincipalMapper,
    formatLawFirmLocation,
    hasForeignCitizenship: hasForeignCitizenshipDefault,
    showInvoiceSubtitle: false,
    supportsPgPreferredBilling: false,
    supportsBillingOverride: false,
    supportsGuestFlow: false,
    historicFetchEntitySideEffects: true,
    includeBillingOverrideInPayload: false,
    headingClassName: "font-bold mb-4",
  },
};

const RETRIEVAL_BILLING_SURFACE_OVERLAY = {
  surface: "retrieval",
  formatLawFirmLocation: (location) => ({
    label: location.name,
    value: location.law_firm_location_id,
    address: location.address,
  }),
  showInvoiceSubtitle: false,
  showPrincipalCustNo: true,
  supportsGuestAutoSelectLawFirm: true,
  supportsPgPreferredBilling: false,
  supportsBillingOverride: false,
  supportsGuestFlow: false,
  historicFetchEntitySideEffects: false,
  includeBillingOverrideInPayload: false,
  includeCustNoInPayload: true,
  useEmptyInitialBillingEntity: true,
  skipScrollToTop: true,
  hasForeignCitizenship: () => false,
  headingClassName: "font-bold mb-4",
  creatableSelectUsesIsDisabled: false,
};

export const BILLING_RETRIEVAL_SURFACE_CONFIG = {
  guardianship: {
    ...RETRIEVAL_BILLING_SURFACE_OVERLAY,
    mapPrincipal: guardianshipPrincipalMapper,
  },
  va: {
    ...RETRIEVAL_BILLING_SURFACE_OVERLAY,
    mapPrincipal: defaultPrincipalMapper,
  },
  trustee: {
    ...RETRIEVAL_BILLING_SURFACE_OVERLAY,
    mapPrincipal: defaultPrincipalMapper,
  },
  probate: {
    ...RETRIEVAL_BILLING_SURFACE_OVERLAY,
    mapPrincipal: defaultPrincipalMapper,
    creatableSelectUsesIsDisabled: true,
  },
};

export function getBillingSurfaceConfig(surface) {
  return BILLING_SURFACE_CONFIG[surface] || BILLING_SURFACE_CONFIG.creation;
}

export function getBillingRetrievalSurfaceConfig(bondType) {
  return BILLING_RETRIEVAL_SURFACE_CONFIG[bondType] || BILLING_RETRIEVAL_SURFACE_CONFIG.va;
}
